/**
 * Relational Onboarding Modal - In-app onboarding for relational focus
 * Similar to the main onboarding but can be triggered from the hub
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  User, Heart, Users, Briefcase, Home, 
  UserPlus, ArrowRight, ArrowLeft, Sparkles
} from 'lucide-react';

interface RelationalFocus {
  connectionType: 'self' | 'partner' | 'both';
  relationshipType?: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  workingMode?: 'solo' | 'together';
}

interface RelationalOnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (focus: RelationalFocus) => void;
  initialFocus?: RelationalFocus | null;
}

const CONNECTION_OPTIONS = [
  {
    id: 'self',
    label: 'Connection to Self',
    description: 'Deepen your relationship with yourself - self-compassion, inner harmony, and self-understanding',
    icon: User,
  },
  {
    id: 'partner',
    label: 'Connection with Another Person',
    description: 'Strengthen your bond with a significant person in your life',
    icon: Heart,
  },
  {
    id: 'both',
    label: 'Both',
    description: 'Work on your connection to yourself and to another person together',
    icon: Users,
  },
];

const RELATIONSHIP_TYPES = [
  {
    id: 'romantic',
    label: 'Romantic Partner',
    description: 'Spouse, partner, or significant other',
    icon: Heart,
  },
  {
    id: 'family',
    label: 'Family Member',
    description: 'Parent, sibling, child, or other family',
    icon: Home,
  },
  {
    id: 'work',
    label: 'Work Relationship',
    description: 'Colleague, manager, or professional relationship',
    icon: Briefcase,
  },
  {
    id: 'friendship',
    label: 'Close Friend',
    description: 'A deep friendship you want to nurture',
    icon: UserPlus,
  },
  {
    id: 'other',
    label: 'Other Relationship',
    description: 'Any meaningful relationship in your life',
    icon: Users,
  },
];

const WORKING_MODES = [
  {
    id: 'solo',
    label: 'Working on My Own',
    description: 'I\'ll do exercises solo and apply learnings in my relationship',
  },
  {
    id: 'together',
    label: 'Working with Partner',
    description: 'We\'ll do exercises together (partner linking coming soon)',
  },
];

export function RelationalOnboardingModal({
  open,
  onOpenChange,
  onComplete,
  initialFocus,
}: RelationalOnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [connectionType, setConnectionType] = useState<'self' | 'partner' | 'both'>(
    initialFocus?.connectionType || 'both'
  );
  const [relationshipType, setRelationshipType] = useState<string>(
    initialFocus?.relationshipType || 'romantic'
  );
  const [workingMode, setWorkingMode] = useState<'solo' | 'together'>(
    initialFocus?.workingMode || 'solo'
  );

  const showRelationshipStep = connectionType === 'partner' || connectionType === 'both';
  const showWorkingModeStep = relationshipType === 'romantic' && showRelationshipStep;

  const totalSteps = showWorkingModeStep ? 3 : showRelationshipStep ? 2 : 1;

  const handleNext = () => {
    if (step === 1 && connectionType === 'self') {
      handleComplete();
    } else if (step === 1 && showRelationshipStep) {
      setStep(2);
    } else if (step === 2 && showWorkingModeStep) {
      setStep(3);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleComplete = () => {
    const focus: RelationalFocus = {
      connectionType,
      ...(showRelationshipStep && { 
        relationshipType: relationshipType as RelationalFocus['relationshipType'] 
      }),
      ...(showWorkingModeStep && { workingMode }),
    };
    onComplete(focus);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 && 'Your Relational Focus'}
            {step === 2 && 'Relationship Type'}
            {step === 3 && 'How Will You Work?'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'What would you like to focus on in your relational journey?'}
            {step === 2 && 'What type of relationship are you focusing on?'}
            {step === 3 && 'Are you working on this alone or together with your partner?'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Connection Type */}
          {step === 1 && (
            <div className="space-y-3">
              {CONNECTION_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = connectionType === option.id;

                return (
                  <Card
                    key={option.id}
                    className={`
                      relative flex items-start space-x-3 p-4 cursor-pointer
                      transition-all hover:border-primary/50
                      ${isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border'}
                    `}
                    onClick={() => setConnectionType(option.id as 'self' | 'partner' | 'both')}
                  >
                    <div className={`
                      p-2 rounded-lg transition-colors
                      ${isSelected ? 'bg-primary/20' : 'bg-muted'}
                    `}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-base font-medium ${isSelected ? 'text-primary' : ''}`}>
                        {option.label}
                      </span>
                      <p className="text-sm text-muted-foreground mt-1">
                        {option.description}
                      </p>
                    </div>
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'}
                    `}>
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Step 2: Relationship Type */}
          {step === 2 && (
            <RadioGroup 
              value={relationshipType} 
              onValueChange={setRelationshipType}
              className="space-y-2"
            >
              {RELATIONSHIP_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.id}
                    className={`
                      flex items-center space-x-3 rounded-lg border p-3 cursor-pointer
                      transition-all hover:border-primary/50
                      ${relationshipType === type.id ? 'border-primary bg-primary/5' : 'border-border'}
                    `}
                    onClick={() => setRelationshipType(type.id)}
                  >
                    <RadioGroupItem value={type.id} id={type.id} />
                    <div className="p-1.5 rounded bg-muted">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <Label htmlFor={type.id} className="flex-1 cursor-pointer">
                      <span className="font-medium">{type.label}</span>
                      <p className="text-xs text-muted-foreground">{type.description}</p>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}

          {/* Step 3: Working Mode */}
          {step === 3 && (
            <RadioGroup 
              value={workingMode} 
              onValueChange={(v) => setWorkingMode(v as 'solo' | 'together')}
              className="space-y-3"
            >
              {WORKING_MODES.map((mode) => (
                <div
                  key={mode.id}
                  className={`
                    flex items-center space-x-3 rounded-lg border p-4 cursor-pointer
                    transition-all hover:border-primary/50
                    ${workingMode === mode.id ? 'border-primary bg-primary/5' : 'border-border'}
                  `}
                  onClick={() => setWorkingMode(mode.id as 'solo' | 'together')}
                >
                  <RadioGroupItem value={mode.id} id={mode.id} />
                  <Label htmlFor={mode.id} className="flex-1 cursor-pointer">
                    <span className="font-medium">{mode.label}</span>
                    <p className="text-xs text-muted-foreground">{mode.description}</p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          {/* Inspirational Note */}
          <Card className="mt-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
            <div className="p-4 flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                {connectionType === 'self' ? (
                  "The relationship you have with yourself sets the tone for every other relationship in your life."
                ) : connectionType === 'partner' ? (
                  "Healthy relationships are built on a foundation of mutual respect, understanding, and conscious communication."
                ) : (
                  "When we nurture both our inner world and our connections with others, we create a life of deeper meaning and harmony."
                )}
              </p>
            </div>
          </Card>
        </div>

        <div className="flex justify-between pt-2">
          {step > 1 ? (
            <Button variant="outline" onClick={handleBack} className="gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
          <Button onClick={handleNext} className="gap-1">
            {step === totalSteps || connectionType === 'self' ? 'Save' : 'Continue'}
            {step < totalSteps && connectionType !== 'self' && <ArrowRight className="w-4 h-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
